import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowRight, ShoppingBag, Share2, Calendar } from 'lucide-react';
import { selectionService, Selection } from '../../services/selection';
import { Button } from '../../components/common/Button';
import moment from 'moment-jalaali';

// Helper to format price
const formatPrice = (price: string | number) => {
  return Number(price).toLocaleString('fa-IR');
};

const SelectionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sharingId, setSharingId] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchSelection(id);
    }
  }, [id]);

  const fetchSelection = async (selectionId: string) => {
    setIsLoading(true);
    try {
      const response = await selectionService.getById(selectionId);
      setSelection(response.data.data);
    } catch (error) {
      console.error('Error fetching selection:', error);
      toast.error('خطا در دریافت جزئیات لیست');
      navigate('/orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = async () => {
    if (!selection) return;

    setSharingId(selection.id);
    try {
      const response = await selectionService.generateShareImage(selection.id);
      const imageUrl = response.image_url;

      if (navigator.share) {
        try {
            const imageBlob = await fetch(imageUrl).then(r => r.blob());
            const file = new File([imageBlob], 'selection.png', { type: 'image/png' });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: 'لیست منتخب من',
                    text: 'لیست محصولات انتخابی من در راز گلد',
                });
            } else {
                 await navigator.share({
                    title: 'لیست منتخب من',
                    text: 'لیست محصولات انتخابی من در راز گلد',
                    url: imageUrl
                });
            }
        } catch (shareError) {
             console.error('Error sharing:', shareError);
        }
      } else {
         // Fallback: Open image in new tab
         window.open(imageUrl, '_blank');
      }
    } catch (error) {
      console.error('Error generating share image:', error);
      toast.error('خطا در ایجاد تصویر اشتراک‌گذاری');
    } finally {
      setSharingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!selection) return null;

  const calculateTotals = () => {
    if (!selection.items) return { count: 0, weight: 0, price: 0 };
    
    return selection.items.reduce((acc, item) => {
      const price = (item.product as any)?.calculated_price?.total_price || 0;
      const weight = item.product?.weight || 0;
      
      return {
        count: acc.count + 1,
        weight: acc.weight + weight,
        price: acc.price + price
      };
    }, { count: 0, weight: 0, price: 0 });
  };

  const totals = calculateTotals();

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/orders', { state: { tab: 'selections' } })}
            className="text-gray-500 hover:text-gray-900"
          >
            <ArrowRight className="w-6 h-6" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">جزئیات لیست</h1>
            <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
              <Calendar className="w-4 h-4" />
              {moment(selection.created_at).format('jYYYY/jMM/jDD HH:mm')}
            </p>
          </div>
        </div>

        <Button 
          variant="outline" 
          onClick={handleShare}
          isLoading={sharingId === selection.id}
          className="gap-2"
        >
          <Share2 className="w-4 h-4" />
          <span className="hidden sm:inline">اشتراک‌گذاری</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Items List */}
        <div className="lg:col-span-2 space-y-4">
          {selection.items.map((item) => (
            <div key={item.product_id} className="card p-4 flex gap-4">
              <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                {item.product?.image_urls?.[0] ? (
                  <img
                    src={item.product.image_urls[0]}
                    alt={item.product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <ShoppingBag className="w-8 h-8" />
                  </div>
                )}
              </div>
              
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-bold text-gray-900 line-clamp-1">{item.product?.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">کد: {item.product?.code}</p>
                </div>
                
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-600">
                    وزن: <span className="font-medium text-gray-900">{item.product?.weight}</span> گرم
                  </div>
                  <div className="text-accent font-bold">
                    {(item.product as any)?.calculated_price ? formatPrice((item.product as any).calculated_price.total_price) : '---'} تومان
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary Card */}
        <div className="lg:col-span-1">
          <div className="card p-6 sticky top-24">
            <h3 className="text-lg font-bold text-gray-900 mb-6">خلاصه لیست</h3>
            
            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-gray-600">
                <span>تعداد اقلام</span>
                <span className="font-medium text-gray-900">{totals.count} عدد</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>وزن کل</span>
                <span className="font-medium text-gray-900">{totals.weight.toFixed(3)} گرم</span>
              </div>
              <div className="divider my-4" />
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-900">مجموع</span>
                <span className="text-xl font-bold text-accent">{formatPrice(totals.price)} تومان</span>
              </div>
            </div>

            <div className="bg-amber-50 text-amber-800 p-4 rounded-lg text-sm mb-6">
              این لیست ثبت شده است و امکان تغییر در آن وجود ندارد.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectionDetailsPage;
